import React, { ChangeEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CustomInput from "../custom-input";
import closeIcon from "../../assests/close.svg";
import CustomSelect from "../custom-select";
import CustomButton from "../custom-button";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/parse-errors";
import axiosInstance from "../../utils/axios-instance";
import Loader from "../loader";
import { User } from "../../providers/auth-provider";
import GetUser from "../../utils/get-user";

interface TCreateAgentModel {
  ToggleModel: () => void;
  isEdit: User | boolean;
}

let userTypes = [
  {
    label: "Super Admin",
    value: "super-admin",
  },
  {
    label: "Admin",
    value: "admin",
  },
  {
    label: "Agent",
    value: "agent",
  },
];

const CreateAgentModel = ({ ToggleModel, isEdit }: TCreateAgentModel) => {
  const user = GetUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [values, setValues] = useState<any>({});
  const isUpdate = typeof isEdit !== "boolean" && isEdit?.id;

  // --------------------------------- [ CREATE USER ] --------------------------------
  const createUser = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.post("/auth/register", values);
      toast.success(res.data.message);
      setIsLoading(false);
      ToggleModel();
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      setIsLoading(false);
    }
  };

  // --------------------------------- [ CREATE USER ] --------------------------------
  const updateUser = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.put(
        `/user/update/${isUpdate && isEdit.id}`,
        values
      );
      toast.success(res.data.message);
      setIsLoading(false);
      ToggleModel();
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      setIsLoading(false);
    }
  };

  // ------------------------------ [ ON CHANGE VALUES ] --------------------------------
  const onChangeValues = (e: ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) ToggleModel();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isLoading, ToggleModel]);

  return createPortal(
    <div
      className="z-[99999] w-screen h-screen fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-center justify-center animate-fade-in"
      onClick={() => !isLoading && ToggleModel()}
    >
      <div
        className="bg-surface border border-edge shadow-overlay min-w-[450px] rounded-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/*---------------------------- [ HEADER ] ------------------------------- */}
        <div className="flex items-center justify-between py-4 px-6 border-b border-edge-faint bg-surface-elevated rounded-t-2xl">
          <h2 className="font-heading-font text-[25px] bg-gradient-heading bg-clip-text text-transparent">
            {isUpdate ? "Update" : "Create"} User
          </h2>
          <button onClick={ToggleModel} className="rounded-full p-1 hover:bg-overlay-hover transition-colors">
            <img
              src={closeIcon}
              alt="Close Img"
              className="cover h-[32px] w-[32px] cursor-pointer"
            />
          </button>
        </div>
        {/*---------------------------- [ HEADER ] ------------------------------- */}

        {/* -------------------------- [ BODY ] ------------------------------------ */}
        <div className="p-8">
          <CustomInput
            autoComplete="none"
            label="Full Name"
            defaultValue={isUpdate ? isEdit?.name : ""}
            name="name"
            onChange={onChangeValues}
          />
          <div className="my-4"></div>
          <CustomInput
            autoComplete="none"
            label="Email"
            defaultValue={isUpdate ? isEdit?.email : ""}
            name="email"
            onChange={onChangeValues}
          />
          <div className="my-4"></div>
          <CustomInput
            autoComplete="none"
            label="Phone"
            defaultValue={isUpdate ? isEdit?.phone : ""}
            name="phone"
            onChange={onChangeValues}
          />
          <div className="my-4"></div>
          <CustomInput
            autoComplete="none"
            label="User Code"
            defaultValue={isUpdate ? isEdit?.code : ""}
            name="code"
            onChange={onChangeValues}
          />
          <div className="my-4"></div>
          <CustomInput
            autoComplete="none"
            label="Password"
            name="password"
            onChange={onChangeValues}
          />
          <div className="my-4"></div>
          <CustomSelect
            comboboxType="select"
            label="User Type"
            labelProperty="label"
            valueProperty="value"
            options={
              user.type === "super-admin"
                ? userTypes
                : userTypes.filter((userT) => userT.value === "agent")
            }
            onSingleSelectChange={(option) =>
              setValues((prev) => ({ ...prev, type: option?.value }))
            }
            value={userTypes.find(
              (type) => type.value === (values?.type || (isEdit as any)?.type)
            )}
          />
          <div className="my-6"></div>
          <CustomButton
            disabled={isLoading}
            fullWidth
            variant="primary"
            className="w-full"
            onClick={isUpdate ? updateUser : createUser}
          >
            {isLoading ? <Loader /> : isUpdate ? "UPDATE" : "SUBMIT"}
          </CustomButton>
        </div>
        {/* -------------------------- [ BODY ] ------------------------------------ */}
      </div>
    </div>,
    document.body
  );
};

export default CreateAgentModel;
